import { type Reference, type Unmasked, gql } from '@apollo/client'

import { apolloClient } from '@/requests/apollo/apolloConfig.ts'
import { INDEPENDENT_CHILDREN_GQL_SCHEMA, STORY_BASE_GQL_SCHEMA } from '@/requests/apollo/schema.ts'

export const cache = apolloClient.cache

export const getStory = (storyId: string): Story | null | undefined =>
  cache.readFragment({
    id: cache.identify({ __typename: 'Story', id: storyId }),
    fragment: gql`fragment ExistingStory on Story {
      ${STORY_BASE_GQL_SCHEMA}
      ${INDEPENDENT_CHILDREN_GQL_SCHEMA}
    }`,
  })

export const getEntity = <T>(entityType: ChildEntity, entityScheme: string, entityId?: string): T | null | undefined =>
  cache.readFragment<T>({
    id: cache.identify({ __typename: entityType, id: entityId }),
    fragment: gql(`fragment Existing${entityType} on ${entityType} {
      ${entityScheme}
    }`),
  }) as T | null | undefined

export const addCacheListElement = <T extends Project | Iteration | Story | ChildEntity | Label | BaseChildEntity>(
  identifier: string | undefined,
  element: T,
  fieldName: string,
  isPrepend = false,
) => {
  cache.modify({
    id: identifier,
    fields: {
      // biome-ignore lint/style/useDefaultParameterLast: This is a GraphQL cache configuration
      [fieldName](existingRefs = [], { toReference }) {
        return isPrepend
          ? [toReference(element, true), ...(existingRefs as Reference[])]
          : [...(existingRefs as Reference[]), toReference(element, true)]
      },
    },
  })
}

export const removeCacheListElement = (identifier: string | undefined, fieldName: string, id: string) => {
  cache.modify({
    id: identifier,
    fields: {
      // biome-ignore lint/style/useDefaultParameterLast: This is a GraphQL cache configuration
      [fieldName]: (existingEntityRefs = [], { readField }) =>
        (existingEntityRefs as Reference[]).filter((entityRef: Reference) => readField('id', entityRef) !== id),
    },
  })
}

export const redefineTaskOrder = (identifier: string | undefined) => {
  cache.modify({
    id: identifier,
    fields: {
      // biome-ignore lint/style/useDefaultParameterLast: This is a GraphQL cache configuration
      tasks(existingEntityRefs = [], { readField }) {
        const refs = existingEntityRefs as Reference[]

        for (let index = 0; index < refs.length; index++) {
          const currentRef = refs[index]
          const previousRef = index > 0 ? refs[index - 1] : null
          const nextRef = index < refs.length - 1 ? refs[index + 1] : null

          cache.modify({
            id: currentRef.__ref,
            fields: {
              afterId() {
                return previousRef ? readField('id', previousRef) : null
              },
              beforeId() {
                return nextRef ? readField('id', nextRef) : null
              },
            },
          })
        }

        return existingEntityRefs
      },
    },
  })
}

export const createCacheEntity = <T>(entity: BaseChildEntity, entityType: ChildEntity, entityScheme: string) => {
  cache.writeFragment({
    id: cache.identify(entity),
    fragment: gql(`fragment New${entityType} on ${entityType} {
      ${entityScheme}
    }`),
    data: entity as Unmasked<T & { __typename: ChildEntity }>,
  })

  cache.modify({
    id: cache.identify({ __typename: 'Story', id: entity.storyId }),
    fields: {
      // biome-ignore lint/style/useDefaultParameterLast: This is a GraphQL cache configuration
      [`${entityType.toLowerCase()}s`](existingRefs = [], { toReference }) {
        return [...(existingRefs as Reference[]), toReference(entity)]
      },
    },
  })
}

export const removeCacheEntity = (entity: BaseChildEntity, entityType: ChildEntity) => {
  cache.modify({
    id: cache.identify({ __typename: 'Story', id: entity.storyId }),
    fields: {
      // biome-ignore lint/style/useDefaultParameterLast: This is a GraphQL cache configuration
      [`${entityType.toLowerCase()}s`]: (existingEntityRefs = [], { readField }) =>
        (existingEntityRefs as Reference[]).filter((entityRef: Reference) => readField('id', entityRef) !== entity.id),
    },
  })
}
